import { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Grid2';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import Autocomplete from '@mui/material/Autocomplete';
import { useConfirm, useToast } from '@radiology/ui';
import dayjs from 'dayjs';
import {
  useAdminModules,
  useForceReset,
  useSetDisabled,
  useUpdateProfile,
  type AdminUser,
} from './api';
import { RoleAssignments } from './RoleAssignments';
import { useLookup, useLookupUnion } from './scopes';
import { useCertificateLists } from '../certifications/lists';

/**
 * One user: their profile, their roles across every module, and the account
 * actions.
 *
 * Roles for modules the caller does not administer are shown but not editable.
 * Showing them matters — an administrator needs to know that the person they are
 * about to disable is the only purchasing approver — and the database refuses the
 * edit anyway, so disabling the control is honesty rather than security.
 */
export function UserDialog({ user, onClose }: { user: AdminUser | null; onClose: () => void }) {
  const adminModules = useAdminModules();
  const updateProfile = useUpdateProfile();
  const setDisabled = useSetDisabled();
  const forceReset = useForceReset();
  const confirm = useConfirm();
  const toast = useToast();

  const [form, setForm] = useState({
    display_name: '',
    hospital: '',
    section: '',
    title: '',
    badge_number: '',
    phone: '',
    department_id: '',
  });

  // Hospital, section and title were dropdowns in the legacy admin form, fed
  // from the attributes table. Department was the one field typed by hand, which
  // is why the migrated department values are inconsistent — it is a lookup now
  // too, spanning both keys the legacy apps used for it.
  const hospitals = useLookup('hospital');
  const sections = useLookup('section');
  const titles = useLookup('title');
  // Read-only here; configured in System settings (CERT-09).
  const lists = useCertificateLists();
  const departments = useLookupUnion(['department', 'department_id']);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    setForm({
      display_name: user.display_name ?? '',
      hospital: user.hospital ?? '',
      section: user.section ?? '',
      title: user.title ?? '',
      badge_number: user.badge_number ?? '',
      phone: user.phone ?? '',
      department_id: user.department_id ?? '',
    });
    setError(null);
  }, [user]);

  if (!user) return <Dialog open={false} onClose={onClose} />;

  const mine = adminModules.data ?? [];

  const save = () => {
    updateProfile.mutate(
      { userId: user.user_id, ...form },
      {
        onSuccess: () => toast.success('Profile updated'),
        onError: (e: Error) => setError(e.message),
      },
    );
  };

  const toggleDisabled = async () => {
    const disabling = !user.disabled;
    if (disabling) {
      const ok = await confirm({
        title: `Disable ${user.username}?`,
        message:
          'They will not be able to sign in. Their data and role grants are kept, so enabling them again restores everything.',
        confirmLabel: 'Disable',
        tone: 'danger',
      });
      if (!ok) return;
    }
    setDisabled.mutate(
      { userId: user.user_id, disabled: disabling },
      {
        onSuccess: () => toast.success(disabling ? 'Account disabled' : 'Account enabled'),
        onError: (e: Error) => setError(e.message),
      },
    );
  };

  const reset = async () => {
    const ok = await confirm({
      title: `Force a password reset for ${user.username}?`,
      message:
        'They will be asked to set a new password the next time they sign in. You are not shown or given the password — they prove control of their mailbox with an emailed code, as at cutover.',
      confirmLabel: 'Force reset',
    });
    if (!ok) return;

    forceReset.mutate(user.user_id, {
      onSuccess: () => toast.success('They will be asked to reset at next sign-in'),
      onError: (e: Error) => setError(e.message),
    });
  };

  const field = (label: string, key: keyof typeof form, size = 6, help?: string) => (
    <Grid size={{ xs: 12, sm: size }}>
      <TextField
        label={label}
        fullWidth
        value={form[key]}
        helperText={help}
        onChange={e => setForm(prev => ({ ...prev, [key]: e.target.value }))}
      />
    </Grid>
  );

  /**
   * Which certificate lists the chosen Title pulls in.
   *
   * The CR makes Title the driver for certificate requirements, and the mapping
   * is an exact (trimmed, case-insensitive) match on the title string. So a
   * title typed by hand that does not match a mapping is not an error — it
   * quietly means the employee is measured against the All-Titles lists alone.
   * That is invisible from here unless the screen says it, which is what the
   * "User management" row of the CR's impacted-screens table is asking for.
   */
  const certificateRequirementHint = (() => {
    if (lists.isLoading) return undefined;

    const general = lists.lists.filter(l => l.all_titles && l.active).map(l => l.name);
    const chosen = form.title.trim().toLowerCase();
    const mapped = chosen
      ? lists.titles
          .filter(t => t.title.trim().toLowerCase() === chosen)
          .map(t => t.list_name)
          .filter(name => lists.lists.some(l => l.name === name && l.active))
      : [];

    if (general.length === 0 && mapped.length === 0) {
      return 'No certificate lists are configured yet, so nothing is required.';
    }
    if (mapped.length === 0) {
      return `Certificates required: ${general.join(', ') || 'none'}. This title has no list of its own.`;
    }
    return `Certificates required: ${[...general, ...mapped].join(', ')}.`;
  })();

  /**
   * A lookup field. `freeSolo` on purpose: the migrated data contains values
   * that are not in the vocabularies — the purchasing manager sits at `test H`,
   * and several device sections are not configured anywhere — and a strict
   * dropdown would make those profiles uneditable, which is the opposite of what
   * this screen is for.
   */
  const lookup = (
    label: string,
    key: keyof typeof form,
    options: string[] | undefined,
    size = 6,
    help?: string,
  ) => (
    <Grid size={{ xs: 12, sm: size }}>
      <Autocomplete
        freeSolo
        options={options ?? []}
        value={form[key]}
        onInputChange={(_e, value) => setForm(prev => ({ ...prev, [key]: value }))}
        renderInput={params => <TextField {...params} label={label} fullWidth helperText={help} />}
      />
    </Grid>
  );

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle variant="h4">{user.display_name || user.username}</DialogTitle>
      <DialogContent>
        <Stack gap={2}>
          <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
            <Chip size="small" label={user.username} />
            {user.email && <Chip size="small" variant="outlined" label={user.email} />}
            {user.disabled && <Chip size="small" color="error" label="Disabled" />}
            {user.must_reset_password && (
              <Chip size="small" color="warning" label="Must reset password" />
            )}
            <Typography variant="caption" color="text.secondary">
              {user.last_sign_in_at
                ? `Last signed in ${dayjs(user.last_sign_in_at).format('D MMM YYYY')}`
                : 'Never signed in'}
            </Typography>
          </Stack>

          {error && <Alert severity="error">{error}</Alert>}

          <Grid container spacing={2}>
            {field('Full name', 'display_name', 12)}
            {lookup(
              'Hospital',
              'hospital',
              hospitals.data,
              6,
              'Purchasing scopes on this, and it is the fallback for an unassigned role',
            )}
            {lookup(
              'Section',
              'section',
              sections.data,
              6,
              'The fallback for a supervisor with no assignment below',
            )}
            {/* Title now decides which certificates this person must hold
                (CERT-07/CERT-10), which is a consequence an administrator
                cannot see anywhere else on this screen. The field stays
                free-text — the vocabulary is not always complete and blocking
                an admin mid-task would be worse — but an unrecognised title
                silently means "General certificates only", so it says so. */}
            {lookup('Title', 'title', titles.data, 6, certificateRequirementHint)}
            {lookup(
              'Department',
              'department_id',
              departments.data,
              6,
              'Directory only — nothing scopes on it (decision #13)',
            )}
            {field('Badge number', 'badge_number')}
            {field('Phone', 'phone', 6)}
          </Grid>

          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="caption" color="text.secondary">
              The username cannot be changed — it joins this person to their rows in every module.
            </Typography>
            <Button variant="outlined" onClick={save} disabled={updateProfile.isPending}>
              {updateProfile.isPending ? 'Saving…' : 'Save profile'}
            </Button>
          </Stack>

          <Divider />

          <RoleAssignments user={user} adminModules={mine} onError={setError} />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button
          color="error"
          onClick={() => void toggleDisabled()}
          disabled={setDisabled.isPending}
        >
          {user.disabled ? 'Enable account' : 'Disable account'}
        </Button>
        <Button color="inherit" onClick={() => void reset()} disabled={forceReset.isPending}>
          Force password reset
        </Button>
        <Stack sx={{ flex: 1 }} />
        <Button variant="contained" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
